/**
 * 初期パスワード変更画面。
 *
 * 管理者に招待されたユーザが初期パスワードでログインすると (Cognito: FORCE_CHANGE_PASSWORD)、
 * AuthProvider が `isNewPasswordRequired` を true にする。App.tsx がこの画面を表示し、
 * 新パスワードを設定すると、そのままログイン済みになってメイン画面が起動する。
 */
import { useState, type FormEvent } from 'react';

import { APP_TITLE } from '../constants/appInfo';

import { useAuth } from './AuthProvider';
import { PasswordRequirements } from './PasswordRequirements';
import { isPasswordValid } from './passwordPolicy';

export function NewPasswordPage(): React.JSX.Element {
  const { completeNewPassword, cancelNewPassword, error: authError } = useAuth();

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setLocalError(null);
    if (!isPasswordValid(password)) {
      setLocalError('パスワードが条件を満たしていません');
      return;
    }
    if (password !== passwordConfirm) {
      setLocalError('パスワードと確認用パスワードが一致しません');
      return;
    }
    setSubmitting(true);
    try {
      // 成功すると AuthProvider が認証済みになり、App 側でメイン画面へ切り替わる
      await completeNewPassword(password);
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage = localError ?? authError;

  return (
    <div className="login-page">
      <div className="login-box">
        <h1>{APP_TITLE}</h1>
        <h2 className="login-subtitle">初期パスワードの変更</h2>
        <form onSubmit={handleSubmit}>
          <div className="info-message" data-testid="new-password-info">
            初期パスワードでログインしました。続行するには、新しいパスワードを設定してください。
          </div>
          <label>
            <span className="required">新しいパスワード</span>
            <input
              type="password"
              data-testid="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
            />
          </label>
          <PasswordRequirements password={password} />
          <label>
            <span className="required">新しいパスワード (確認)</span>
            <input
              type="password"
              data-testid="new-password-confirm"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              disabled={submitting}
            />
          </label>
          {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
          <button
            type="submit"
            data-testid="new-password-submit"
            className="primary"
            disabled={submitting || !password || !passwordConfirm}
          >
            {submitting ? '設定中...' : 'パスワードを変更して開始'}
          </button>
          <div className="login-footer">
            <button
              type="button"
              data-testid="new-password-cancel"
              onClick={cancelNewPassword}
              disabled={submitting}
            >
              ログイン画面へ戻る
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
